/**
 * Shared ProcureIQ issue codes for Ops / Christos (mirrors API eligibility.py).
 */

const DEFAULT_SUPY_ITEM_SUPPLIERS_URL =
  "https://retailer.supy.io/items/{retailer_item_id}/suppliers";

export const ORDER_ISSUE_META = {
  excluded: {
    title: "Excluded (config)",
    explain: "Item is on the never-order list (env config).",
    detail:
      "Hard exclude from engineering config (PROCUREIQ_EXCLUDED_LEAF_ITEM_IDS). The leaf forecast may still show need, but week-orders never builds a line. Not the same as Ops day exclude or Discontinued.",
    fix: "Remove the Supy item id from PROCUREIQ_EXCLUDED_LEAF_ITEM_IDS if it should order.",
  },
  excluded_config: {
    title: "Excluded (config)",
    explain: "Item is on the never-order list (env config).",
    detail:
      "Hard exclude from engineering config (PROCUREIQ_EXCLUDED_LEAF_ITEM_IDS). The leaf forecast may still show need, but week-orders never builds a line. Not the same as Ops day exclude or Discontinued.",
    fix: "Remove the Supy item id from PROCUREIQ_EXCLUDED_LEAF_ITEM_IDS if it should order.",
  },
  excluded_override: {
    title: "Excluded (Ops override)",
    explain: "Manager marked this product excluded for the day.",
    detail:
      "A day-level Ops override excluded this product_id for the order date. Stock/forecast still exist; only today’s basket line is suppressed until Clear exclude.",
    fix: "Clear exclude below to put it back in the basket.",
  },
  inactive_ledger: {
    title: "No recent ledger activity",
    explain:
      "No consumption-like ledger movement at this store in the activity window (default 28 days). Often a menu ghost.",
    detail:
      "Soft gate: published leaf need exists, but this store has no use/waste/transfer-out style ledger rows in the activity window (default PROCUREIQ_LEDGER_ACTIVE_DAYS=28). Common for menu ghosts or items sold only elsewhere. Keep forces order; Discontinued hides forever at this store.",
    fix: "Mark Keep (still order) or Discontinued below. Or check Supy POS→ledger sync if sales should appear.",
  },
  discontinued: {
    title: "Marked discontinued",
    explain: "Ops marked this inventory item discontinued at this store — never ordered.",
    detail:
      "Stored in procureiq_item_status for this unified_location_id + item_id. Week-orders and leaf eligibility skip the item until status is cleared or set to Keep.",
    fix: "Clear status (or mark Keep) in Order build if it should order again.",
  },
  no_catalog_product: {
    title: "Missing catalog / channel bridge",
    explain:
      "No procureiq_products row and no usable store channel (or channel supplier name ≠ ProcureIQ suppliers).",
    detail:
      "Channel-first path: if Supy has an active channel offer at this store whose supplier_name exactly matches an active procureiq_suppliers row, we synthesize a basket line without procureiq_products. This code means neither a catalog row nor that bridge was available.",
    fix: "Add Supy Suppliers channel at this store (name must match a ProcureIQ supplier), or Mark Discontinued if ghost.",
  },
  sfg_not_purchased: {
    title: "In-house prep (SFG)",
    explain:
      "Semi-finished / prep item with no Supy buy channel at this store — usually made in-house, not purchased.",
    detail:
      "Item type is semi-finished / prep / recipe and there is no buy channel at this store. Forecast may still allocate usage (made in kitchen). Not a missing-catalog bug — do not add procureiq_products just to silence this.",
    fix: "Mark Discontinued if it should never appear; otherwise ignore (not a catalog gap).",
  },
  no_catalog_supplier: {
    title: "Catalog has no supplier",
    explain: "Product is in the catalog but supplier_id is empty.",
    detail:
      "procureiq_products exists and is active, but supplier_id is null/invalid so we cannot attach lead time, min order, or schedule. Channel-first cannot run without a catalog supplier name either when no channel offers exist.",
    fix: "Set supplier on the procureiq_products row.",
  },
  catalog_supplier_no_channel: {
    title: "Catalog supplier, no store channel",
    explain: "Catalog has a supplier, but this store has no active Supy channel offer.",
    detail:
      "Catalog points at a supplier, but supy_stg_channel_items has no active pack/price for this inventory item at this store. Ordering needs the Supy Suppliers tab offer (then channel ETL). Until then the line is dropped.",
    fix: "In Supy → item → Suppliers: add preferred pack/price for this store, then wait for channel sync.",
    checklist: [
      "Open Suppliers tab for this item",
      "Active channel offer at this store?",
      "Pack + price set?",
      "Wait for channel sync, then Refresh",
    ],
  },
  no_channel_offer: {
    title: "No channel offer",
    explain: "No active Supy supplier channel pack/price at this store.",
    detail:
      "Same practical gap as catalog_supplier_no_channel when the catalog has no supplier name: week-orders requires at least one active channel pack for the item at this location.",
    fix: "In Supy → item → Suppliers: add an active channel offer at this store.",
    checklist: [
      "Open Suppliers tab for this item",
      "Add / activate channel for this store",
      "Pack + price present?",
      "Wait for channel sync, then Refresh",
    ],
  },
  no_channel_supplier_match: {
    title: "Supplier name mismatch (legacy)",
    explain:
      "Older builds dropped when catalog ≠ channel supplier. Ordering now picks the channel supplier automatically.",
    detail:
      "Legacy code required catalog supplier_name to equal a channel supplier. Current builds rematch to the channel supplier (AFM prefixes allowed). If you still see this, refresh week orders or ignore a cached payload.",
    fix: "Refresh week orders — should no longer block. Safe to ignore if still listed from a cached week.",
  },
  no_channel_package: {
    title: "No mappable pack",
    explain: "Channel offer exists but cannot map to a packaging / order unit.",
    detail:
      "A channel row exists but units_per_pack / packaging id could not be resolved for order qty (broken packaging link or empty pack metadata). Fix the pack on the Supy Suppliers tab or packaging dim, then re-sync.",
    fix: "Fix packaging ↔ channel pack mapping in Supy.",
    checklist: [
      "Open Suppliers tab for this item",
      "Channel pack matches a known packaging?",
      "Base unit / case size look right?",
      "Fix in Supy, wait for sync, then Refresh",
    ],
  },
  stale_leaf_forecast_cache: {
    title: "Stale leaf cache",
    explain: "Old leaf_forecast cache row is not in this week’s published leaf.",
    detail:
      "A recipe-cache / old leaf_forecast row survived for an item that is not in this week’s published procureiq_forecast_leaf_w. Usually a removed menu ingredient. Safe to ignore after republish.",
    fix: "Republish leaf week, or ignore — usually a menu ghost.",
  },
  not_orderable_day: {
    title: "Not an order day",
    explain: "Supplier schedule says today is not an ordering day.",
    detail:
      "Supplier has an ordering-days calendar in Ops; this weekday is not orderable. Need still rolls to the next orderable slot in the week when schedule split is on. Adjust schedule only if the calendar is wrong.",
    fix: "Wait for the next orderable day, or adjust supplier schedule in Ops.",
  },
  duplicate_channel_item: {
    title: "Duplicate channel line",
    explain: "Same channel item already placed for this supplier.",
    detail:
      "Dedup guard: the same inventory item was already added under this channel supplier for the day (e.g. two catalog rows mapping to one channel item). Keeping one line avoids double-ordering.",
    fix: "Usually safe to ignore.",
  },
  store_product_filtered: {
    title: "Store assortment filter",
    explain: "Blocked by procureiq_store_product_config for this store.",
    detail:
      "This location has an active assortment allowlist. The catalog product_id is not enabled here (or inactive). Channel-bridged lines skip this filter. Use Enable at this store to upsert an active config row.",
    fix: "Enable at this store below if it should be orderable here.",
  },
};

/** Codes where Christos fixes in Supy → Suppliers (show checklist). */
export const SUPY_CHECKLIST_CODES = new Set([
  "catalog_supplier_no_channel",
  "no_channel_offer",
  "no_channel_package",
]);

export function orderIssueMeta(code) {
  const key = String(code || "").trim();
  if (!key) return null;
  return (
    ORDER_ISSUE_META[key] || {
      title: key,
      explain: "Dropped from the week basket for this reason.",
      fix: "Check Raw materials Issue column for the same code.",
    }
  );
}

export function orderIssueTooltip(code) {
  const meta = orderIssueMeta(code);
  if (!meta) return "";
  return `${meta.explain} Fix: ${meta.fix}`;
}

/** Longer hover copy for IssueCodeChip (EN; GR uses same detail for now). */
export function orderIssueDetailTooltip(code, lang = "en") {
  const meta = orderIssueMeta(code);
  if (!meta) return "";
  const gr = lang === "gr";
  const detail = meta.detail || meta.explain;
  const fixLabel = gr ? "Διόρθωση" : "Fix";
  const parts = [detail, `${fixLabel}: ${meta.fix}`];
  if (meta.checklist?.length) {
    parts.push((gr ? "Checklist" : "Checklist") + ":\n• " + meta.checklist.join("\n• "));
  }
  return parts.join("\n\n");
}

/**
 * Parse drop samples:
 * - "Name (inventoryId / retailerId)" (preferred)
 * - "Name (inventoryId)" (legacy — inventory only, no Supy link)
 */
export function parseDropSample(sample) {
  const text = String(sample || "").trim();
  if (!text) return { name: "", itemId: null, retailerItemId: null };
  const both = text.match(
    /^(.*)\s+\(([a-f0-9]{20,})\s*\/\s*([a-f0-9]{20,})\)\s*$/i
  );
  if (both) {
    return {
      name: both[1].trim() || both[2],
      itemId: both[2],
      retailerItemId: both[3],
    };
  }
  const one = text.match(/^(.*)\s+\(([a-f0-9]{20,})\)\s*$/i);
  if (one) {
    return { name: one[1].trim() || one[2], itemId: one[2], retailerItemId: null };
  }
  return { name: text, itemId: null, retailerItemId: null };
}

/**
 * Format last ledger activity for Christos (Greek DD/MM/YYYY + days ago).
 * @param {string|null|undefined} isoDate
 * @param {number|null|undefined} daysAgo
 * @param {{ lang?: string, known?: boolean }} opts
 *   known=false → API did not send the field (do not claim “none”).
 */
export function formatLastLedgerActivity(isoDate, daysAgo, { lang = "en", known = true } = {}) {
  const gr = lang === "gr";
  if (!known) {
    return gr ? "Τελευταία κίνηση: —" : "Last activity: —";
  }
  if (!isoDate) {
    return gr
      ? "Τελευταία κίνηση: καμία (2 έτη)"
      : "Last activity: none (in 2y lookback)";
  }
  const parts = String(isoDate).slice(0, 10).split("-");
  const label =
    parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : String(isoDate).slice(0, 10);
  const n = Number(daysAgo);
  if (Number.isFinite(n)) {
    if (n <= 0) {
      return gr ? `Τελευταία κίνηση: σήμερα (${label})` : `Last activity: today (${label})`;
    }
    return gr
      ? `Τελευταία κίνηση: ${label} (πριν ${n} μέρες)`
      : `Last activity: ${label} (${n}d ago)`;
  }
  return gr ? `Τελευταία κίνηση: ${label}` : `Last activity: ${label}`;
}

/** Supy UI uses retailer_item_id (not inventory item_id). */
export function supyItemSuppliersUrl(retailerItemId) {
  const id = String(retailerItemId || "").trim();
  if (!id) return null;
  const template =
    process.env.REACT_APP_SUPY_UI_ITEM_SUPPLIERS_URL_TEMPLATE ||
    DEFAULT_SUPY_ITEM_SUPPLIERS_URL;
  return template
    .replace(/\{retailer_item_id\}/g, encodeURIComponent(id))
    .replace(/\{item_id\}/g, encodeURIComponent(id));
}

const DEFAULT_SUPY_ORDER_URL = "https://retailer.supy.io/orders/{order_id}";

/**
 * Deep link to Supy purchase order (Mongo order id).
 * Prefer Ops order view + "Show on Supy" for store staff who lack Supy login.
 */
export function supyOrderUrl(orderId) {
  const id = String(orderId || "").trim();
  if (!id) return null;
  const template =
    process.env.REACT_APP_SUPY_UI_ORDER_URL_TEMPLATE || DEFAULT_SUPY_ORDER_URL;
  return template
    .replace(/\{order_id\}/g, encodeURIComponent(id))
    .replace(/\{id\}/g, encodeURIComponent(id));
}
